import {useCallback, useEffect, useState} from "react";
import {connect, useSelector} from "react-redux";
import {RouteComponentProps, useLocation, withRouter} from "react-router";
import {css, Theme} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {calculateRemSize, display, flex, onDesktop, pt} from "@pg-design/helpers-css";
import {FiltersIcon} from "@pg-design/icons-module";
import {FormActions, IFormValuesAction} from "@pg-mono/form";
import {appendQueryString} from "@pg-mono/request";
import {Scenario} from "@pg-mono/rp-api-routes";
import {BUTTONS_TEST_ID} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {MobileOptionsModal} from "../../../atoms/MobileOptionsModal";
import {useTransitioningHistoryPush} from "../../../hooks/use_transitioning_history_push";
import {resetPrivilegedOfferList} from "../../../offer/list/actions/fetch_privileged_offer_list";
import {OfferListSortMobile} from "../../../offer/list/components/OfferListsSortMobile";
import {SearchSortString} from "../../../offer/list/constants/SearchSort";
import {trackAmplitudeListingFiltersApplied} from "../../../offer/tracking/amplitude/track_amplitude_listing_filters_applied";
import {hitFiltersInteraction} from "../../../real_estate/tracking/hit_filters_interaction";
import {convertSearchFormToFiltersTracking} from "../../../real_estate/utils/convert_search_form_to_filters_tracking";
import {resetSearchForm, searchFormActions} from "../../../search/actions/search_form_actions";
import {MobileTopSearchTriggers} from "../../../search/components/MobileTopSearchTriggers";
import {SearchFilters} from "../../../search/components/SearchFilters";
import {searchFormFields} from "../../../search/constants/search_form_constants";
import {useCountSearchFilters} from "../../../search/hooks/use_count_search_filters";
import {useFilter} from "../../../search/hooks/use_filter";
import {useMobileFilters} from "../../../search/hooks/use_mobile_filters";
import useSearchAndFilterUpdate from "../../../search/hooks/use_search_and_filter_update";
import {ISearchFiltersFormValues} from "../../../search/types/ISearchFiltersFormValues";
import {SearchFormValuesType} from "../../../search/types/SearchFormValuesType";
import {getSearchFiltersByOfferType} from "../../../search/utils/get_search_filters_by_offer_type";
import {getStringQueryValuesFromFormValues} from "../../../search/utils/get_string_values_from_form_values";
import {useUserSearchPreferences} from "../../../user/hooks/use_user_search_preferences";
import {ILatestQuery} from "../../../utils/latest_query";
import {ViewType} from "../../../view_type/ViewType";
import {getInvestmentOfferListLink, getInvestmentOfferListMapLink} from "../../utils/get_abroad_offer_redirect_link";
import {getParsedInvestmentOfferListUrl} from "../../utils/get_parsed_investment_offer_list_url";

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    resetPrivilegedOfferList: typeof resetPrivilegedOfferList;
    resetSearchForm: (query: Record<string, string>) => Promise<IFormValuesAction<Record<string, unknown>> | undefined>;
}
interface IOwnProps extends RouteComponentProps<{}> {
    onAfterSubmit?: () => void;
    latestQuery: ILatestQuery;
    isAbroad: boolean;
    mapUrl?: string;
    listUrl?: string;
    isMap?: boolean;
}

interface IProps extends IOwnProps, IActionsProps {}

const InvestmentOfferFiltersDrawerC = (props: IProps) => {
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const scenario = viewType.current === ViewType.FOR_INVESTMENT_LIST ? Scenario.OFFER_LIST : Scenario.OFFER_LIST_MAP;
    const {transitionHistory} = useTransitioningHistoryPush();
    const location = useLocation();
    const {isMobile} = useUserDevice();
    const {saveUserPreferences} = useUserSearchPreferences();
    const formValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;

    useEffect(() => {
        const query = props.latestQuery as Record<string, string>;
        (async () => {
            await props.resetSearchForm(query);
        })();
    }, []);

    const {isMobileFiltersActive, closeMobileFilterModal, toggleMobileFilterModal, prevFilterState} = useMobileFilters({
        ...props,
        formValues,
        onClose: (makeGtmHit?: boolean) => {
            makeGtmHit && hitFiltersInteraction("close", convertSearchFormToFiltersTracking(formValues));
        }
    });

    const onSubmit = useCallback(
        async (formValues: SearchFormValuesType) => {
            trackAmplitudeListingFiltersApplied({
                formValues: formValues as ISearchFiltersFormValues,
                latestQuery: props.latestQuery,
                eventParams: {filter_trigger: "manual", location: "investment_page"}
            });

            const stringFormQueryValues = getStringQueryValuesFromFormValues(formValues);

            await saveUserPreferences({
                filters: {
                    ...stringFormQueryValues
                }
            });

            const params = getParsedInvestmentOfferListUrl(location.pathname);
            const url = props.isMap ? getInvestmentOfferListMapLink(params) : getInvestmentOfferListLink(params);

            if (url) {
                const investmentUrl = appendQueryString(url, stringFormQueryValues);

                transitionHistory(investmentUrl);
            }
        },
        [scenario, props.latestQuery, props.isAbroad]
    );

    const {onAfterChange} = useSearchAndFilterUpdate({
        resetPrivilegedOfferList: resetPrivilegedOfferList,
        formValues: formValues,
        latestQuery: props.latestQuery,
        onSubmit: (formValues: SearchFormValuesType) => onSubmit(formValues)
    });

    const {isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters} = useFilter({
        formValues: formValues,
        formActions: props.formActions,
        closeMobileFilterModal: closeMobileFilterModal,
        onAfterChange: onAfterChange,
        onSubmit: onSubmit
    });

    const searchFiltersConfig = getSearchFiltersByOfferType(formValues.type?.[0]);
    const {totalCount: numberOfSearchFilters} = useCountSearchFilters(formValues, searchFiltersConfig);

    const mobileFiltersProps = {
        selectedRegionsCount: formValues["search"].regions ? formValues["search"].regions.length : 0,
        selectedFiltersCount: numberOfSearchFilters,
        onToggleFiltersClick: toggleMobileFilterModal,
        onToggleSortClick: () => null,
        latestQuery: props.latestQuery,
        firstSelectedRegion: formValues.search?.regions?.[0]?.name,
        mapUrl: props.mapUrl,
        listUrl: props.listUrl
    };

    return (
        <>
            <div css={filtersButtonsStyle}>
                <Button
                    size="small"
                    variant="filled_primary"
                    iconLeft={FiltersIcon}
                    css={[actionButtonStyle, display("none"), onDesktop(display("inline-flex"))]}
                    onClick={() => setDrawerOpen(true)}
                    dataTestId={BUTTONS_TEST_ID.OPEN_FILTERS_BUTTON}
                >
                    FILTRY
                    {numberOfSearchFilters > 0 ? (
                        <Badge variant="label_danger" css={filtersCountBadge}>
                            {numberOfSearchFilters}
                        </Badge>
                    ) : null}
                </Button>

                <SearchFilters
                    useFilterHook={{isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters}}
                    useMobileFilterHook={{isMobileFiltersActive, closeMobileFilterModal, prevFilterState, toggleMobileFilterModal}}
                    latestQuery={props.latestQuery}
                    handleSubmit={() => onSubmit(formValues)}
                    isAbroad={props.isAbroad}
                    recentFiltersLocation="investment_page"
                    values={formValues}
                    onChange={props.formActions.update}
                    onAfterChange={onAfterChange}
                    onSubmit={() => onSubmit(formValues)}
                />

                {isMobile && (
                    <>
                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={() => setMobileSortActive(false)}>
                            <OfferListSortMobile
                                name="mobile-sort"
                                onSubmit={() => setMobileSortActive(false)}
                                latestQuerySort={props.latestQuery.sort as SearchSortString}
                                onMobileResetForm={resetFilters}
                            />
                        </MobileOptionsModal>
                        <MobileTopSearchTriggers
                            {...mobileFiltersProps}
                            disableAlert
                            isAbroad={props.isAbroad}
                            sortActive
                            onToggleSortClick={() => setMobileSortActive((prev) => !prev)}
                            showLocationSearch={false}
                        />
                    </>
                )}
            </div>
        </>
    );
};

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({resetPrivilegedOfferList, resetSearchForm}, dispatch)
    };
}

export const InvestmentOfferFiltersDrawer = withRouter(connect<{}, IActionsProps, IOwnProps, IRPStore>(null, mapActionsToProps)(InvestmentOfferFiltersDrawerC));

const filtersButtonsStyle = (theme: Theme) => css`
    ${flex()};
    justify-content: end;
    gap: ${calculateRemSize(2)};
    ${pt(0.5)};
    @media (min-width: ${theme.breakpoints.md}) {
        ${pt(0)}
    }
`;
const actionButtonStyle = css`
    overflow: visible;
    width: 18rem;
`;
const filtersCountBadge = css`
    position: absolute;
    right: -${calculateRemSize(0.5)};
    top: -${calculateRemSize(0.5)};
`;
import {useCallback, useEffect, useState} from "react";
import {connect, useSelector} from "react-redux";
import {RouteComponentProps, useLocation, withRouter} from "react-router";
import {css, Theme} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {calculateRemSize, display, flex, onDesktop, pt} from "@pg-design/helpers-css";
import {FiltersIcon} from "@pg-design/icons-module";
import {FormActions, IFormValuesAction} from "@pg-mono/form";
import {appendQueryString} from "@pg-mono/request";
import {Scenario} from "@pg-mono/rp-api-routes";
import {BUTTONS_TEST_ID} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {MobileOptionsModal} from "../../../atoms/MobileOptionsModal";
import {useTransitioningHistoryPush} from "../../../hooks/use_transitioning_history_push";
import {resetPrivilegedOfferList} from "../../../offer/list/actions/fetch_privileged_offer_list";
import {OfferListSortMobile} from "../../../offer/list/components/OfferListsSortMobile";
import {SearchSortString} from "../../../offer/list/constants/SearchSort";
import {trackAmplitudeListingFiltersApplied} from "../../../offer/tracking/amplitude/track_amplitude_listing_filters_applied";
import {hitFiltersInteraction} from "../../../real_estate/tracking/hit_filters_interaction";
import {convertSearchFormToFiltersTracking} from "../../../real_estate/utils/convert_search_form_to_filters_tracking";
import {resetSearchForm, searchFormActions} from "../../../search/actions/search_form_actions";
import {MobileTopSearchTriggers} from "../../../search/components/MobileTopSearchTriggers";
import {SearchFilters} from "../../../search/components/SearchFilters";
import {searchFormFields} from "../../../search/constants/search_form_constants";
import {useCountSearchFilters} from "../../../search/hooks/use_count_search_filters";
import {useFilter} from "../../../search/hooks/use_filter";
import {useMobileFilters} from "../../../search/hooks/use_mobile_filters";
import useSearchAndFilterUpdate from "../../../search/hooks/use_search_and_filter_update";
import {ISearchFiltersFormValues} from "../../../search/types/ISearchFiltersFormValues";
import {SearchFormValuesType} from "../../../search/types/SearchFormValuesType";
import {getSearchFiltersByOfferType} from "../../../search/utils/get_search_filters_by_offer_type";
import {getStringQueryValuesFromFormValues} from "../../../search/utils/get_string_values_from_form_values";
import {useUserSearchPreferences} from "../../../user/hooks/use_user_search_preferences";
import {ILatestQuery} from "../../../utils/latest_query";
import {ViewType} from "../../../view_type/ViewType";
import {getInvestmentOfferListLink, getInvestmentOfferListMapLink} from "../../utils/get_abroad_offer_redirect_link";
import {getParsedInvestmentOfferListUrl} from "../../utils/get_parsed_investment_offer_list_url";

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    resetPrivilegedOfferList: typeof resetPrivilegedOfferList;
    resetSearchForm: (query: Record<string, string>) => Promise<IFormValuesAction<Record<string, unknown>> | undefined>;
}
interface IOwnProps extends RouteComponentProps<{}> {
    onAfterSubmit?: () => void;
    latestQuery: ILatestQuery;
    isAbroad: boolean;
    mapUrl?: string;
    listUrl?: string;
    isMap?: boolean;
}

interface IProps extends IOwnProps, IActionsProps {}

const InvestmentOfferFiltersDrawerC = (props: IProps) => {
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const scenario = viewType.current === ViewType.FOR_INVESTMENT_LIST ? Scenario.OFFER_LIST : Scenario.OFFER_LIST_MAP;
    const {transitionHistory} = useTransitioningHistoryPush();
    const location = useLocation();
    const {isMobile} = useUserDevice();
    const {saveUserPreferences} = useUserSearchPreferences();
    const formValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;

    useEffect(() => {
        const query = props.latestQuery as Record<string, string>;
        (async () => {
            await props.resetSearchForm(query);
        })();
    }, []);

    const {isMobileFiltersActive, closeMobileFilterModal, toggleMobileFilterModal, prevFilterState} = useMobileFilters({
        ...props,
        formValues,
        onClose: (makeGtmHit?: boolean) => {
            makeGtmHit && hitFiltersInteraction("close", convertSearchFormToFiltersTracking(formValues));
        }
    });

    const onSubmit = useCallback(
        async (formValues: SearchFormValuesType) => {
            trackAmplitudeListingFiltersApplied({
                formValues: formValues as ISearchFiltersFormValues,
                latestQuery: props.latestQuery,
                eventParams: {filter_trigger: "manual", location: "investment_page"}
            });

            const stringFormQueryValues = getStringQueryValuesFromFormValues(formValues);

            await saveUserPreferences({
                filters: {
                    ...stringFormQueryValues
                }
            });

            const params = getParsedInvestmentOfferListUrl(location.pathname);
            const url = props.isMap ? getInvestmentOfferListMapLink(params) : getInvestmentOfferListLink(params);

            if (url) {
                const investmentUrl = appendQueryString(url, stringFormQueryValues);

                transitionHistory(investmentUrl);
            }
        },
        [scenario, props.latestQuery, props.isAbroad]
    );

    const {onAfterChange} = useSearchAndFilterUpdate({
        resetPrivilegedOfferList: resetPrivilegedOfferList,
        formValues: formValues,
        latestQuery: props.latestQuery,
        onSubmit: (formValues: SearchFormValuesType) => onSubmit(formValues)
    });

    const {isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters} = useFilter({
        formValues: formValues,
        formActions: props.formActions,
        closeMobileFilterModal: closeMobileFilterModal,
        onAfterChange: onAfterChange,
        onSubmit: onSubmit
    });

    const searchFiltersConfig = getSearchFiltersByOfferType(formValues.type?.[0]);
    const {totalCount: numberOfSearchFilters} = useCountSearchFilters(formValues, searchFiltersConfig);

    const mobileFiltersProps = {
        selectedRegionsCount: formValues["search"].regions ? formValues["search"].regions.length : 0,
        selectedFiltersCount: numberOfSearchFilters,
        onToggleFiltersClick: toggleMobileFilterModal,
        onToggleSortClick: () => null,
        latestQuery: props.latestQuery,
        firstSelectedRegion: formValues.search?.regions?.[0]?.name,
        mapUrl: props.mapUrl,
        listUrl: props.listUrl
    };

    return (
        <>
            <div css={filtersButtonsStyle}>
                <Button
                    size="small"
                    variant="filled_primary"
                    iconLeft={FiltersIcon}
                    css={[actionButtonStyle, display("none"), onDesktop(display("inline-flex"))]}
                    onClick={() => setDrawerOpen(true)}
                    dataTestId={BUTTONS_TEST_ID.OPEN_FILTERS_BUTTON}
                >
                    FILTRY
                    {numberOfSearchFilters > 0 ? (
                        <Badge variant="label_danger" css={filtersCountBadge}>
                            {numberOfSearchFilters}
                        </Badge>
                    ) : null}
                </Button>

                <SearchFilters
                    useFilterHook={{isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters}}
                    useMobileFilterHook={{isMobileFiltersActive, closeMobileFilterModal, prevFilterState, toggleMobileFilterModal}}
                    latestQuery={props.latestQuery}
                    handleSubmit={() => onSubmit(formValues)}
                    isAbroad={props.isAbroad}
                    recentFiltersLocation="investment_page"
                    values={formValues}
                    onChange={props.formActions.update}
                    onAfterChange={onAfterChange}
                    onSubmit={() => onSubmit(formValues)}
                />

                {isMobile && (
                    <>
                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={() => setMobileSortActive(false)}>
                            <OfferListSortMobile
                                name="mobile-sort"
                                onSubmit={() => setMobileSortActive(false)}
                                latestQuerySort={props.latestQuery.sort as SearchSortString}
                                onMobileResetForm={resetFilters}
                            />
                        </MobileOptionsModal>
                        <MobileTopSearchTriggers
                            {...mobileFiltersProps}
                            disableAlert
                            isAbroad={props.isAbroad}
                            sortActive
                            onToggleSortClick={() => setMobileSortActive((prev) => !prev)}
                            showLocationSearch={false}
                        />
                    </>
                )}
            </div>
        </>
    );
};

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({resetPrivilegedOfferList, resetSearchForm}, dispatch)
    };
}

export const InvestmentOfferFiltersDrawer = withRouter(connect<{}, IActionsProps, IOwnProps, IRPStore>(null, mapActionsToProps)(InvestmentOfferFiltersDrawerC));

const filtersButtonsStyle = (theme: Theme) => css`
    ${flex()};
    justify-content: end;
    gap: ${calculateRemSize(2)};
    ${pt(0.5)};
    @media (min-width: ${theme.breakpoints.md}) {
        ${pt(0)}
    }
`;
const actionButtonStyle = css`
    overflow: visible;
    width: 18rem;
`;
const filtersCountBadge = css`
    position: absolute;
    right: -${calculateRemSize(0.5)};
    top: -${calculateRemSize(0.5)};
`;
